import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  SectionCard,
  FieldLabel,
  InfoRow,
  StatusBadge,
  UploadBox,
  ActionButtonGroup,
  WarningBanner,
  ApproverRoleBadge,
  EmptyState,
  useConfirm,
} from "./shared/components";
import {
  getNoDuesTasks,
  submitNoDuesAction,
  NoDuesTask,
  NoDuesChecklistItem,
} from "./services/eSeparation.service";

type ItemStatus = "Returned" | "Not Returned" | "Not Applicable" | "Pending";

const DEFAULT_CHECKLIST: NoDuesChecklistItem[] = [
  { item_id: "1", item_name: "Laptop / Computer", status: "Pending" },
  { item_id: "2", item_name: "Official ID Card", status: "Pending" },
  { item_id: "3", item_name: "Official Files / Documents", status: "Pending" },
  { item_id: "4", item_name: "Mobile Phone (Official)", status: "Pending" },
  { item_id: "5", item_name: "Office Keys", status: "Pending" },
  { item_id: "6", item_name: "Uniform / PPE", status: "Pending" },
  { item_id: "7", item_name: "SIM Card (Official)", status: "Pending" },
  { item_id: "8", item_name: "Books / Manuals", status: "Pending" },
  { item_id: "9", item_name: "Tools / Equipment", status: "Pending" },
  { item_id: "10", item_name: "Other Items", status: "Pending" },
];

const STATUS_OPTIONS: { label: string; value: ItemStatus; color: string }[] = [
  { label: "Returned", value: "Returned", color: "#049123" },
  { label: "Not Returned", value: "Not Returned", color: "#FF0F0F" },
  { label: "N/A", value: "Not Applicable", color: "#78746D" },
];

const NodalAction: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<NoDuesTask[]>([]);
  const [taskId, setTaskId] = useState("");
  const [checklist, setChecklist] = useState<NoDuesChecklistItem[]>(DEFAULT_CHECKLIST);
  const [itemRemarks, setItemRemarks] = useState<Record<string, string>>({});
  const [recoveryAmount, setRecoveryAmount] = useState("");
  const [justification, setJustification] = useState("");
  const [overallRemarks, setOverallRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const load = () => {
    getNoDuesTasks().then((res) => setTasks(res.data || []));
  };
  useEffect(load, []);

  const task = useMemo(() => tasks.find((t) => t.task_id === taskId) || null, [tasks, taskId]);

  useEffect(() => {
    if (task) {
      setChecklist(task.checklist && task.checklist.length > 0 ? task.checklist : DEFAULT_CHECKLIST);
      setRecoveryAmount(task.recovery_amount > 0 ? String(task.recovery_amount) : "");
      setOverallRemarks(task.remarks || "");
    } else {
      setChecklist(DEFAULT_CHECKLIST);
    }
  }, [task]);

  const setStatus = (id: string, status: ItemStatus) =>
    setChecklist((prev) => prev.map((it) => (it.item_id === id ? { ...it, status } : it)));

  const counts = {
    Returned: checklist.filter((i) => i.status === "Returned").length,
    "Not Returned": checklist.filter((i) => i.status === "Not Returned").length,
    NA: checklist.filter((i) => i.status === "Not Applicable").length,
    Pending: checklist.filter((i) => i.status === "Pending").length,
  };

  const handleAction = async (action: "Cleared" | "Dues Found" | "Clarification") => {
    if (!task) return toast.warning("Please select a task first.");
    const pending = checklist.filter((i) => i.status === "Pending").length;
    if (pending > 0 && action !== "Clarification")
      return toast.warning(`${pending} item(s) are still pending. Please mark all items before proceeding.`);
    if (action === "Dues Found" && !overallRemarks.trim())
      return toast.warning("Please provide remarks when marking dues found.");
    const actionLabels: Record<typeof action, string> = {
      Cleared: "clear this department",
      "Dues Found": "flag dues pending",
      Clarification: "request clarification",
    };
    if (
      !(await confirm(`Are you sure you want to ${actionLabels[action]}?`, {
        title: `Confirm ${action}`,
        confirmLabel: "Confirm",
        tone: action === "Cleared" ? "success" : action === "Dues Found" ? "warning" : "primary",
      }))
    )
      return;
    setLoading(true);
    const res = await submitNoDuesAction({
      task_id: task.task_id,
      action,
      checklist: checklist.map((it) => ({ ...it, remarks: itemRemarks[it.item_id] || it.remarks })),
      recovery_amount: Number(recoveryAmount) || 0,
      remarks: overallRemarks,
    });
    setLoading(false);
    if (res.success) {
      toast.success(`Department clearance has been ${action === "Cleared" ? "approved" : action === "Dues Found" ? "flagged with dues" : "sent for clarification"}.`);
      setTaskId("");
      setOverallRemarks("");
      setRecoveryAmount("");
      load();
    } else {
      toast.error(res.message || "Failed to submit.");
    }
  };

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content">
        <PageHeader
          title="No-Dues Task"
          right={<ApproverRoleBadge role="Nodal" />}
          onExit={() => navigate(-1)}
        />

        {/* Sub-header */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <span
            className="d-inline-flex align-items-center justify-content-center"
            style={{ width: 36, height: 36, borderRadius: 10, background: "#FDE8E0", color: "#993C1D" }}
          >
            <i className="ti ti-clipboard-list" style={{ fontSize: 18 }} />
          </span>
          <div>
            <div className="fw-semibold">{task?.dept_name || "Department"} Clearance</div>
            <small className="text-muted">
              Review and process No-Dues for {task?.emp_name || "employee"}
            </small>
          </div>
        </div>

        {/* Selector */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body">
            <div className="d-flex gap-2 align-items-end">
              <div className="flex-grow-1">
                <FieldLabel label="Select Pending No-Dues Case" required />
                <select className="form-select" value={taskId} onChange={(e) => setTaskId(e.target.value)}>
                  <option value="">Choose a pending task...</option>
                  {tasks.map((t) => (
                    <option key={t.task_id} value={t.task_id}>
                      {t.dept_name} - {t.emp_name} ({t.emp_no})
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn btn-outline-secondary" onClick={load}>
                <i className="ti ti-refresh" />
              </button>
            </div>
          </div>
        </div>

        {!task ? (
          <EmptyState icon="ti-clipboard" message="Please select a clearance task from the dropdown above." />
        ) : (
          <>
            {/* Employee info */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 40, height: 40, borderRadius: 12, background: "#E6F1FB" }}>
                    <i className="ti ti-user" style={{ color: "#185FA5" }} />
                  </span>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{task.emp_name || "—"}</div>
                    <small className="text-muted">{task.emp_no} • {task.designation || "—"}</small>
                  </div>
                  <StatusBadge status={task.status} />
                </div>
                <InfoRow label="Case ID" value={task.nd_case_id} />
                <InfoRow label="Case Type" value={task.case_type} />
                <InfoRow label="Assigned Date" value={task.assigned_date} />
              </div>
            </div>

            {/* Progress */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <div className="fw-semibold mb-2">Checklist Progress</div>
                <div className="row g-2">
              {[
                { label: "Returned", count: counts.Returned, color: "#049123" },
                { label: "Not Returned", count: counts["Not Returned"], color: "#FF0F0F" },
                { label: "N/A", count: counts.NA, color: "#78746D" },
                { label: "Pending", count: counts.Pending, color: "#BA7517" },
              ].map((p) => (
                <div className="col-3" key={p.label}>
                  <div className="card border-0 shadow-sm text-center" style={{ borderRadius: 10, background: `${p.color}15` }}>
                    <div className="card-body py-2">
                      <div className="fw-bold" style={{ color: p.color }}>{p.count}</div>
                      <div style={{ fontSize: 10, color: p.color }} className="text-uppercase fw-semibold">{p.label}</div>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              </div>
            </div>

            {/* Checklist */}
            <SectionCard index={0} title="Items Checklist">
              <WarningBanner type="info" message="Mark each item's status. Items flagged as 'Not Returned' will appear in the employee's dues." />
              {checklist.map((item, i) => (
                <div key={item.item_id} className="py-2 border-bottom">
                  <div className="fw-medium mb-2">
                    {i + 1}. {item.item_name}
                  </div>
                  <div className="d-flex gap-2">
                    {STATUS_OPTIONS.map((opt) => {
                      const active = item.status === opt.value;
                      return (
                        <button
                          key={opt.value}
                          className="btn btn-sm flex-grow-1"
                          style={{
                            border: `1px solid ${active ? opt.color : "#dee2e6"}`,
                            background: active ? `${opt.color}15` : "#fff",
                            color: active ? opt.color : "#6c757d",
                            fontWeight: active ? 600 : 400,
                          }}
                          onClick={() => setStatus(item.item_id, opt.value)}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {item.status === "Not Returned" && (
                    <input
                      className="form-control form-control-sm mt-2"
                      placeholder="Remarks for this item..."
                      value={itemRemarks[item.item_id] || ""}
                      onChange={(e) => setItemRemarks((prev) => ({ ...prev, [item.item_id]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </SectionCard>

            {/* Recovery */}
            <SectionCard index={1} title="Recovery Amount">
              <FieldLabel label="Recovery Amount (₹)" />
              <input type="number" className="form-control mb-2" value={recoveryAmount} onChange={(e) => setRecoveryAmount(e.target.value)} placeholder="Enter recovery amount if applicable" />
              {Number(recoveryAmount) > 0 && (
                <>
                  <FieldLabel label="Justification" required />
                  <textarea className="form-control" rows={2} value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Describe what the recovery is for..." />
                </>
              )}
            </SectionCard>

            {/* Documents */}
            <SectionCard index={2} title="Supporting Documents">
              <p className="text-muted small mb-2">
                Upload receipts, handover proofs, or other supporting documents.
              </p>
              <UploadBox hint="PDF, JPG, PNG — max 5 MB each" />
            </SectionCard>

            {/* Remarks */}
            <SectionCard index={3} title="Overall Remarks">
              <FieldLabel label="Remarks / Comments" />
              <textarea className="form-control" rows={3} value={overallRemarks} onChange={(e) => setOverallRemarks(e.target.value)} placeholder="Enter your overall assessment..." />
            </SectionCard>

            {/* Actions */}
            <SectionCard index={4} title="Take Action">
              <ActionButtonGroup
                onApprove={() => handleAction("Cleared")}
                onReject={() => handleAction("Dues Found")}
                onClarification={() => handleAction("Clarification")}
                loading={loading}
              />
            </SectionCard>
          </>
        )}
      </div>
    </div>
  );
};

export default NodalAction;
